/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Building2,
  Users2,
  Plus,
  ChevronRight,
  ChevronDown,
  Upload,
  Search,
  Mail,
  Phone,
  Briefcase,
  Loader,
  AlertCircle,
  Trash2,
  Edit2,
  X,
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../../hooks/use-toast';
import {
  getContactOrganizations,
  getContactTeams,
  getContacts,
  createContactOrganization,
  createContactTeam,
  createContact,
  deleteContactOrganization,
  deleteContactTeam,
  deleteContact,
} from '../../lib/api/contacts';
import type {
  ContactOrganization,
  ContactTeam,
  Contact,
  ContactOrgType,
} from '@shared/schema';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const ORG_TYPE_LABELS: Record<ContactOrgType, string> = {
  customer: 'Customer',
  partner: 'Partner',
  internal: 'Internal',
  vendor: 'Vendor',
  other: 'Other',
};

const ORG_TYPE_COLORS: Record<ContactOrgType, string> = {
  customer: 'bg-green-100 text-green-800',
  partner: 'bg-blue-100 text-blue-800',
  internal: 'bg-purple-100 text-purple-800',
  vendor: 'bg-orange-100 text-orange-800',
  other: 'bg-gray-100 text-gray-800',
};

interface TreeState {
  expandedOrgs: Set<string>;
  expandedTeams: Set<string>;
}

const EMAIL_REGEX = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;

type ParsedQuickAddLine =
  | { kind: 'organization'; name: string }
  | { kind: 'contact'; name: string; email?: string }
  | { kind: 'invalid'; reason: string; raw: string };

function parseQuickAddLine(raw: string): ParsedQuickAddLine {
  const trimmed = raw.trim();

  if (/^(org|organization)\s*:/i.test(trimmed)) {
    const name = trimmed.replace(/^(org|organization)\s*:\s*/i, '').trim();
    if (!name) {
      return { kind: 'invalid', reason: 'Organization name is empty after "org:".', raw };
    }
    return { kind: 'organization', name };
  }

  if (/^contact\s*:/i.test(trimmed)) {
    const rest = trimmed.replace(/^contact\s*:\s*/i, '').trim();
    const parts = rest.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) {
      return { kind: 'invalid', reason: 'Contact details are missing after "contact:".', raw };
    }
    const name = parts[0];
    const email = parts[1];
    if (email && !EMAIL_REGEX.test(email)) {
      return { kind: 'invalid', reason: `"${email}" is not a valid email address.`, raw };
    }
    return { kind: 'contact', name, email };
  }

  const emailMatch = trimmed.match(EMAIL_REGEX);
  if (emailMatch) {
    const email = emailMatch[1];
    const namePart = trimmed.replace(emailMatch[0], '').replace(/[<>,;]/g, '').trim();
    const name = namePart || email.split('@')[0];
    return { kind: 'contact', name, email };
  }

  // No prefix, no email: treat as organization name (most common intent for a free-form entry).
  if (trimmed.length > 0) {
    return { kind: 'organization', name: trimmed };
  }

  return { kind: 'invalid', reason: 'Line is empty.', raw };
}

export function ContactsTab() {
  const { activeProduct } = useApp();
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<ContactOrganization[]>([]);
  const [teams, setTeams] = useState<Record<string, ContactTeam[]>>({});
  const [contacts, setContacts] = useState<Record<string, Contact[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [treeState, setTreeState] = useState<TreeState>({
    expandedOrgs: new Set(),
    expandedTeams: new Set(),
  });

  // Quick Request Form state
  const [quickRequest, setQuickRequest] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

  // Dialog state
  const [showOrgDialog, setShowOrgDialog] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  // Form state
  const [newOrg, setNewOrg] = useState({ name: '', orgType: 'other' as ContactOrgType, description: '', website: '' });
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', role: '', notes: '' });

  const loadData = useCallback(async () => {
    if (!activeProduct?.id) return;

    setLoading(true);
    setError(null);

    try {
      const orgsResult = await getContactOrganizations(activeProduct.id);
      if (!orgsResult.success || !orgsResult.data) {
        throw new Error(orgsResult.error || 'Failed to load organizations');
      }
      setOrganizations(orgsResult.data);

      const teamsMap: Record<string, ContactTeam[]> = {};
      const contactsMap: Record<string, Contact[]> = {};

      for (const org of orgsResult.data) {
        const teamsResult = await getContactTeams(org.id);
        if (teamsResult.success && teamsResult.data) {
          teamsMap[org.id] = teamsResult.data;
        }

        const contactsResult = await getContacts({ organizationId: org.id });
        if (contactsResult.success && contactsResult.data) {
          contactsMap[org.id] = contactsResult.data as Contact[];
        }
      }

      setTeams(teamsMap);
      setContacts(contactsMap);
    } catch (err: any) {
      setError(err.message || 'Failed to load contacts data');
    } finally {
      setLoading(false);
    }
  }, [activeProduct?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleOrg = (orgId: string) => {
    setTreeState(prev => {
      const newExpanded = new Set(prev.expandedOrgs);
      if (newExpanded.has(orgId)) {
        newExpanded.delete(orgId);
      } else {
        newExpanded.add(orgId);
      }
      return { ...prev, expandedOrgs: newExpanded };
    });
  };

  const toggleTeam = (teamId: string) => {
    setTreeState(prev => {
      const newExpanded = new Set(prev.expandedTeams);
      if (newExpanded.has(teamId)) {
        newExpanded.delete(teamId);
      } else {
        newExpanded.add(teamId);
      }
      return { ...prev, expandedTeams: newExpanded };
    });
  };

  const handleQuickRequest = async () => {
    if (!quickRequest.trim()) {
      toast({
        title: 'Nothing to add',
        description: 'Enter at least one organization or contact.',
        variant: 'destructive',
      });
      return;
    }

    if (!activeProduct?.id) {
      toast({
        title: 'No active product',
        description: 'Select a product before adding contacts.',
        variant: 'destructive',
      });
      return;
    }

    const lines = quickRequest.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) {
      toast({
        title: 'Nothing to add',
        description: 'Enter at least one organization or contact.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmittingRequest(true);

    const parsed = lines.map(parseQuickAddLine);
    let orgsCreated = 0;
    let contactsCreated = 0;
    const errors: string[] = [];

    // Contacts created in this batch need to land somewhere. Track the organization
    // we should attach unqualified contacts to (newly created in this batch, or
    // an existing organization if one exists).
    let latestOrgIdForContacts: string | undefined = organizations[0]?.id;

    try {
      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        const lineNumber = i + 1;

        if (item.kind === 'invalid') {
          errors.push(`Line ${lineNumber}: ${item.reason}`);
          continue;
        }

        if (item.kind === 'organization') {
          try {
            const result = await createContactOrganization({
              productId: activeProduct.id,
              name: item.name,
              orgType: 'other',
            });
            if (result.success && result.data) {
              orgsCreated++;
              latestOrgIdForContacts = result.data.id;
            } else {
              errors.push(`Line ${lineNumber}: ${result.error || `Failed to create organization "${item.name}".`}`);
            }
          } catch (err: any) {
            errors.push(`Line ${lineNumber}: ${err.message || `Failed to create organization "${item.name}".`}`);
          }
          continue;
        }

        // kind === 'contact'
        if (!latestOrgIdForContacts) {
          errors.push(
            `Line ${lineNumber}: Cannot add contact "${item.name}" because no organization exists yet. Add an organization first (e.g. "org: Acme Corp").`
          );
          continue;
        }

        try {
          const result = await createContact({
            organizationId: latestOrgIdForContacts,
            name: item.name,
            email: item.email,
          });
          if (result.success) {
            contactsCreated++;
          } else {
            errors.push(`Line ${lineNumber}: ${result.error || `Failed to create contact "${item.name}".`}`);
          }
        } catch (err: any) {
          errors.push(`Line ${lineNumber}: ${err.message || `Failed to create contact "${item.name}".`}`);
        }
      }

      const totalCreated = orgsCreated + contactsCreated;

      if (totalCreated > 0) {
        const parts: string[] = [];
        if (orgsCreated > 0) parts.push(`${orgsCreated} organization${orgsCreated === 1 ? '' : 's'}`);
        if (contactsCreated > 0) parts.push(`${contactsCreated} contact${contactsCreated === 1 ? '' : 's'}`);

        toast({
          title: errors.length > 0 ? 'Partial success' : 'Added',
          description:
            `Created ${parts.join(' and ')}.` +
            (errors.length > 0 ? ` ${errors.length} line${errors.length === 1 ? '' : 's'} skipped.` : ''),
          variant: errors.length > 0 ? 'destructive' : 'default',
        });
        setQuickRequest('');
        await loadData();
      } else {
        toast({
          title: 'Nothing was added',
          description: errors[0] || 'No lines could be parsed. Use "org: Name", "contact: Name, email", a plain email, or a plain organization name.',
          variant: 'destructive',
        });
      }

      if (errors.length > 0) {
        // Also expose full detail via the inline error banner so the user can read all issues.
        setError(errors.join('\n'));
      }
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!activeProduct?.id || !newOrg.name.trim()) return;

    try {
      await createContactOrganization({
        productId: activeProduct.id,
        name: newOrg.name.trim(),
        orgType: newOrg.orgType,
        description: newOrg.description.trim() || undefined,
        website: newOrg.website.trim() || undefined,
      });
      setNewOrg({ name: '', orgType: 'other', description: '', website: '' });
      setShowOrgDialog(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create organization');
    }
  };

  const handleCreateTeam = async () => {
    if (!selectedOrgId || !newTeam.name.trim()) return;

    try {
      await createContactTeam({
        organizationId: selectedOrgId,
        name: newTeam.name.trim(),
        description: newTeam.description.trim() || undefined,
      });
      setNewTeam({ name: '', description: '' });
      setShowTeamDialog(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create team');
    }
  };

  const handleCreateContact = async () => {
    if (!selectedOrgId || !newContact.name.trim()) return;

    try {
      await createContact({
        organizationId: selectedOrgId,
        teamId: selectedTeamId || undefined,
        name: newContact.name.trim(),
        email: newContact.email.trim() || undefined,
        phone: newContact.phone.trim() || undefined,
        role: newContact.role.trim() || undefined,
        notes: newContact.notes.trim() || undefined,
      });
      setNewContact({ name: '', email: '', phone: '', role: '', notes: '' });
      setShowContactDialog(false);
      setSelectedOrgId(null);
      setSelectedTeamId(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create contact');
    }
  };

  const handleDeleteOrg = async (orgId: string) => {
    if (!confirm('Delete this organization and all its teams and contacts?')) return;
    try {
      await deleteContactOrganization(orgId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete organization');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!confirm('Delete this team? Contacts will be moved to the organization level.')) return;
    try {
      await deleteContactTeam(teamId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete team');
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!confirm('Delete this contact?')) return;
    try {
      await deleteContact(contactId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete contact');
    }
  };

  const filteredOrganizations = organizations.filter(org => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (org.name.toLowerCase().includes(query)) return true;
    
    const orgContacts = contacts[org.id] || [];
    return orgContacts.some(c => 
      c.name.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.role?.toLowerCase().includes(query)
    );
  });

  if (!activeProduct) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
        <p>Select a product to manage contacts</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick Request Form */}
      <div className="bg-slate-50 p-4 rounded-lg border">
        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Quick Add
        </h4>
        <div className="flex gap-2">
          <Textarea
            placeholder={
              'Enter organizations or contacts (one per line). Examples:\n' +
              'Acme Corp               (creates an organization)\n' +
              'org: New York Life      (explicit organization)\n' +
              'contact: John Doe, john@example.com\n' +
              'jane@company.com        (adds contact to the most recent organization)'
            }
            value={quickRequest}
            onChange={(e) => setQuickRequest(e.target.value)}
            className="h-24 resize-none text-sm font-mono"
          />
          <Button 
            onClick={handleQuickRequest}
            disabled={!quickRequest.trim() || isSubmittingRequest}
            className="shrink-0"
          >
            {isSubmittingRequest ? <Loader className="h-4 w-4 animate-spin" /> : 'Add'}
          </Button>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)}>
                  <Upload className="h-4 w-4 mr-1" />
                  Import
                </Button>
              </TooltipTrigger>
              <TooltipContent>Import contacts from CSV or Google</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button size="sm" onClick={() => setShowOrgDialog(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Organization
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-red-700 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <pre className="flex-1 whitespace-pre-wrap font-sans text-sm m-0">{error}</pre>
          <button onClick={() => setError(null)} className="shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-8">
          <Loader className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading contacts...</p>
        </div>
      ) : filteredOrganizations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Building2 className="mx-auto h-12 w-12 mb-4 opacity-50" />
          <p className="mb-2">No organizations yet</p>
          <p className="text-sm">Add your first organization to get started</p>
        </div>
      ) : (
        /* Organization Tree */
        <div className="border rounded-lg divide-y">
          {filteredOrganizations.map(org => (
            <div key={org.id} className="bg-white">
              {/* Organization Row */}
              <div 
                className="flex items-center gap-2 p-3 hover:bg-slate-50 cursor-pointer"
                onClick={() => toggleOrg(org.id)}
              >
                {treeState.expandedOrgs.has(org.id) ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <Building2 className="h-5 w-5 text-blue-500" />
                <span className="font-medium flex-1">{org.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${ORG_TYPE_COLORS[org.orgType as ContactOrgType]}`}>
                  {ORG_TYPE_LABELS[org.orgType as ContactOrgType]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {(contacts[org.id] || []).length} contacts
                </span>
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => {
                            setSelectedOrgId(org.id);
                            setShowTeamDialog(true);
                          }}
                        >
                          <Users2 className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Add Team</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => {
                            setSelectedOrgId(org.id);
                            setSelectedTeamId(null);
                            setShowContactDialog(true);
                          }}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Add Contact</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteOrg(org.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete Organization</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {/* Expanded Content */}
              {treeState.expandedOrgs.has(org.id) && (
                <div className="pl-6 border-l-2 border-slate-100 ml-4">
                  {/* Teams */}
                  {(teams[org.id] || []).map(team => (
                    <div key={team.id}>
                      <div 
                        className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggleTeam(team.id)}
                      >
                        {treeState.expandedTeams.has(team.id) ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <Users2 className="h-4 w-4 text-purple-500" />
                        <span className="text-sm flex-1">{team.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {(contacts[org.id] || []).filter(c => c.teamId === team.id).length} members
                        </span>
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => {
                              setSelectedOrgId(org.id);
                              setSelectedTeamId(team.id);
                              setShowContactDialog(true);
                            }}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteTeam(team.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Team Contacts */}
                      {treeState.expandedTeams.has(team.id) && (
                        <div className="pl-6">
                          {(contacts[org.id] || [])
                            .filter(c => c.teamId === team.id)
                            .map(contact => (
                              <ContactRow 
                                key={contact.id} 
                                contact={contact} 
                                onDelete={() => handleDeleteContact(contact.id)} 
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Org-level Contacts (no team) */}
                  {(contacts[org.id] || [])
                    .filter(c => !c.teamId)
                    .map(contact => (
                      <ContactRow 
                        key={contact.id} 
                        contact={contact} 
                        onDelete={() => handleDeleteContact(contact.id)} 
                      />
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {!loading && organizations.length > 0 && (
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground pt-2">
          <div>
            <span className="font-medium">{organizations.length}</span> organizations
          </div>
          <div>
            <span className="font-medium">{Object.values(teams).flat().length}</span> teams
          </div>
          <div>
            <span className="font-medium">{Object.values(contacts).flat().length}</span> contacts
          </div>
        </div>
      )}

      {/* Add Organization Dialog */}
      <Dialog open={showOrgDialog} onOpenChange={setShowOrgDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Organization</DialogTitle>
            <DialogDescription>
              Create a new contact organization for this product.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="org-name">Name *</Label>
              <Input
                id="org-name"
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                placeholder="e.g., Acme Corporation"
              />
            </div>
            <div>
              <Label htmlFor="org-type">Type</Label>
              <Select value={newOrg.orgType} onValueChange={(v) => setNewOrg({ ...newOrg, orgType: v as ContactOrgType })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ORG_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="org-description">Description</Label>
              <Textarea
                id="org-description"
                value={newOrg.description}
                onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
                placeholder="Brief description..."
              />
            </div>
            <div>
              <Label htmlFor="org-website">Website</Label>
              <Input
                id="org-website"
                value={newOrg.website}
                onChange={(e) => setNewOrg({ ...newOrg, website: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrgDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateOrg} disabled={!newOrg.name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Team Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team</DialogTitle>
            <DialogDescription>
              Create a new team within the organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="team-name">Name *</Label>
              <Input
                id="team-name"
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                placeholder="e.g., Engineering"
              />
            </div>
            <div>
              <Label htmlFor="team-description">Description</Label>
              <Textarea
                id="team-description"
                value={newTeam.description}
                onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                placeholder="Brief description..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} disabled={!newTeam.name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
            <DialogDescription>
              Add a new contact to the organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div>
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <Label htmlFor="contact-role">Role</Label>
              <Input
                id="contact-role"
                value={newContact.role}
                onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                placeholder="e.g., Product Manager"
              />
            </div>
            <div>
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                value={newContact.notes}
                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateContact} disabled={!newContact.name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Contacts</DialogTitle>
            <DialogDescription>
              Import contacts from external sources.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button className="w-full justify-start" variant="outline" disabled>
              <Upload className="h-4 w-4 mr-2" />
              Upload CSV File
              <span className="ml-auto text-xs text-muted-foreground">(Coming soon)</span>
            </Button>
            <Button className="w-full justify-start" variant="outline" disabled>
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
              </svg>
              Import from Google Contacts
              <span className="ml-auto text-xs text-muted-foreground">(Coming soon)</span>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContactRow({ contact, onDelete }: { contact: Contact; onDelete: () => void }) {
  return (
    <div className="flex items-center gap-2 p-2 hover:bg-slate-50 text-sm">
      <Users className="h-4 w-4 text-slate-400" />
      <span className="flex-1">{contact.name}</span>
      {contact.role && (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Briefcase className="h-3 w-3" />
          {contact.role}
        </span>
      )}
      {contact.email && (
        <a 
          href={`mailto:${contact.email}`} 
          className="text-xs text-blue-500 hover:underline flex items-center gap-1"
          onClick={e => e.stopPropagation()}
        >
          <Mail className="h-3 w-3" />
          {contact.email}
        </a>
      )}
      {contact.phone && (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Phone className="h-3 w-3" />
          {contact.phone}
        </span>
      )}
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-6 w-6 text-red-500 hover:text-red-600"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
}
